"use client";

import { Button } from "@/components/ui/button";
import { Filters } from "@/components/ui/filters";
import {
  RunSearchButton,
  isRerunnablePhase,
  type RunSearchButtonProps,
} from "@/components/search/run-search-button";
import { useOptionalTalentSearchResults } from "@/components/search/talent-search-results-provider";

/**
 * Save is Run: the filter panel's bottom row. Holds the explicit Run search
 * button and flags the draft as unsaved while it differs from the saved
 * definition, with a Discard action that drops the edits and returns to the
 * saved definition. Inside a `TalentSearchResultsProvider` the button is
 * driven by the session's draft; outside it (the hero, which has no saved
 * search yet) the caller supplies `fallback` props so the same slot creates
 * and runs a new search. Renders nothing when neither is available.
 */
export function RunSearchFooter({
  fallback,
}: {
  fallback?: RunSearchButtonProps;
}) {
  const results = useOptionalTalentSearchResults();
  const button: RunSearchButtonProps | null = results
    ? {
        dirty: results.dirty,
        isEmpty: results.isEmpty,
        failed: isRerunnablePhase(results.run?.phase),
        pending: results.isRunPending,
        onRun: results.runNow,
      }
    : fallback ?? null;
  if (!button) return null;

  // "Unsaved changes" only makes sense against a saved definition.
  const showUnsaved = !!results && button.dirty;

  return (
    <Filters.Footer
      data-testid="run-search-footer"
      className="flex h-12 items-center justify-between gap-3"
    >
      {showUnsaved && results ? (
        <div className="flex min-w-0 items-center gap-2">
          <span
            role="status"
            className="inline-flex h-5 shrink-0 items-center rounded-full border border-ai/25 bg-ai/10 px-2 text-xs font-medium text-ai"
          >
            Unsaved changes
          </span>
          <Button
            variant="ghost"
            size="xs"
            className="text-muted-foreground hover:text-heading"
            disabled={results.isRunPending}
            onClick={results.resetDraft}
          >
            Discard
          </Button>
        </div>
      ) : (
        <span aria-hidden />
      )}
      <RunSearchButton {...button} />
    </Filters.Footer>
  );
}
